import {
  type Placement,
  type ReferenceType,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import { type MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { type PopoverBehaviour, usePopover } from './popover';

export { type Placement };

const DEFAULT_OFFSET = 4;
const ARROW_HEIGHT = 5;

export type UseAnchoredPopoverOptions = {
  /**
   * The behaviour of the popover.
   *
   * @default 'auto'
   */
  behaviour?: PopoverBehaviour;
  /**
   * The placement of the popover.
   *
   * @default 'bottom-start'
   */
  placement?: Placement;

  /**
   * Enables an arrow element pointing to the reference.
   *
   * @default false
   */
  withArrow?: boolean;
  /**
   * Callback triggered on the popover ToggleEvent.
   */
  onToggle?: (event: ToggleEvent) => void;
};

export type UseAnchoredPopoverResults = {
  /**
   * Whether the popover is open.
   */
  isOpen: boolean;
  /**
   * Calls `showPopover` or `hidePopover` depending on the current state.
   */
  togglePopover: () => void;
  /**
   * Calls showPopover() on the current ref.
   */
  showPopover: () => void;
  /**
   * Calls hidePopover() on the current ref.
   */
  hidePopover: () => void;
  /**
   * Props to spread on the anchor element.
   */
  anchorProps: {
    ref: (node: ReferenceType | null) => void;
  };
  /**
   * Props to spread on the popover element.
   */
  popoverProps: {
    popover: PopoverBehaviour;
    ref: (node: HTMLElement | null) => void;
    style: {
      transform: string;
    };
  };
  /**
   * Props to spread on the arrow element.
   */
  popoverArrowProps?: {
    ref: MutableRefObject<HTMLDivElement | null>;
    style: {
      left?: string;
      top?: string;
      right?: string;
      bottom?: string;
      transform?: string;
    };
  };
  /**
   * The reference elements.
   */
  elements: {
    reference: ReferenceType | null;
    floating: HTMLElement | null;
  };
};

export function useAnchoredPopover({
  placement = 'bottom-start',
  behaviour = 'auto',
  withArrow = false,
  onToggle,
}: UseAnchoredPopoverOptions): UseAnchoredPopoverResults {
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const middleware = useMemo(() => {
    const middlewares = [
      flip(),
      shift(),
      offset(withArrow ? DEFAULT_OFFSET + ARROW_HEIGHT : DEFAULT_OFFSET),
    ];

    if (withArrow) {
      middlewares.push(arrow({ element: arrowRef }));
    }
    return middlewares;
  }, [withArrow]);

  const {
    refs,
    update,
    elements,
    x,
    y,
    middlewareData,
    placement: resolvedPlacement,
  } = useFloating({
    strategy: 'fixed',
    placement,
    middleware,
  });

  const { isOpen, togglePopover, popoverProps, hidePopover, showPopover } =
    usePopover({
      ref: refs.floating,
      behaviour,
      onToggle,
    });

  /**
   * This is needed in order to only update the position when the popover is open
   *
   * @see https://floating-ui.com/docs/autoUpdate#usage
   */
  useEffect(() => {
    if (isOpen && elements.reference && elements.floating) {
      const cleanup = autoUpdate(elements.reference, elements.floating, update);
      return cleanup;
    }
  }, [isOpen, elements, update]);

  const _showPopover = () => {
    showPopover();

    /**
     * Calls update() on the next repaint to ensure that the popover is positioned correctly.
     * This removes flickering on the first open if reduce-motion is enabled.
     */
    requestAnimationFrame(() => {
      update();
    });
  };

  const anchorProps = useMemo(() => {
    return {
      ref: refs.setReference,
    };
  }, [refs.setReference]);

  const anchoredPopoverProps = useMemo(() => {
    return {
      ...popoverProps,
      ref: refs.setFloating,
      style: {
        transform: `translateX(${x}px) translateY(${y}px)`,
      },
    };
  }, [popoverProps, refs.setFloating, x, y]);

  const popoverArrowProps = useMemo(() => {
    if (isOpen) {
      const side = resolvedPlacement.split('-')[0];
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[side] as string;

      const arrowOffset = arrowRef.current?.offsetWidth || 0;
      const arrowX = middlewareData.arrow?.x;
      const arrowY = middlewareData.arrow?.y;

      return {
        ref: arrowRef,
        'data-arrow': true,
        style: {
          left: x != null ? `${arrowX}px` : '',
          top: y != null ? `${arrowY}px` : '',
          bottom: '',
          right: '',
          [staticSide]: `${-arrowOffset / 2}px`,
          transform: 'rotate(45deg)',
        },
      };
    }
  }, [
    isOpen,
    middlewareData.arrow?.x,
    middlewareData.arrow?.y,
    resolvedPlacement,
    x,
    y,
  ]);

  return {
    elements,
    anchorProps,
    popoverProps: anchoredPopoverProps,
    popoverArrowProps,
    togglePopover,
    showPopover: _showPopover,
    hidePopover,
    isOpen,
  };
}
